import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CustomsCalculationsService, CustomsCalculation, SearchCustomsCalculationsRequest } from '../../../services/api/customs-calculations';
import { PageResponse } from '../../../services/api/page-response';
import { LanguageService, LanguageCode } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { Subscription } from 'rxjs';

const NUMBER_LOCALES: Record<LanguageCode, string> = { ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' };

/** Кабинет клиента (вход по телефону): список его расчётов в каркасе платформы. */
@Component({
  selector: 'app-public-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PaginationComponent],
  templateUrl: './public-dashboard.html',
  styleUrl: './public-dashboard.scss'
})
export class PublicDashboardComponent implements OnInit, OnDestroy {
  calculations: CustomsCalculation[] = [];
  calculationsPage: PageResponse<CustomsCalculation> | null = null;
  loading = false;
  error: string | null = null;

  currentPage = 0;
  pageSize = 20;
  currentFilters: SearchCustomsCalculationsRequest = {};

  // Фильтры: поиск по названию всегда виден, период — под кнопкой
  title = '';
  dateFrom: string | null = null;
  dateTo: string | null = null;
  filtersOpen = false;

  /** Расчёт, для которого открыто подтверждение удаления. */
  confirmDeleteId: number | null = null;
  deleting = false;
  actionError: string | null = null;

  private numberLocale = 'ru-RU';
  private subscriptions = new Subscription();

  constructor(
    private customsCalculationsService: CustomsCalculationsService,
    private languageService: LanguageService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        this.router.navigate(['/login']);
        return;
      }
      if (this.authService.getUser()?.role?.code !== 'ROLE_PUBLIC') {
        this.router.navigate(['/']);
        return;
      }

      this.loadCalculations();
      this.subscriptions.add(
        this.languageService.getCurrentLanguage().subscribe(lang => {
          this.numberLocale = NUMBER_LOCALES[lang] ?? 'ru-RU';
          this.cdr.markForCheck();
        })
      );
      this.subscriptions.add(
        this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck())
      );
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  get hasDateFilter(): boolean {
    return !!(this.dateFrom || this.dateTo);
  }

  get hasActiveFilters(): boolean {
    return !!(this.title.trim() || this.currentFilters.title || this.currentFilters.dateFrom || this.currentFilters.dateTo);
  }

  /**
   * Сервер ищет только по названию расчёта, а оно обычно пустое — в карточке видно имя товара.
   * Когда все расчёты уже на одной странице, ищем сами по видимому названию и номеру, сразу при вводе.
   */
  get searchLocally(): boolean {
    return !!this.calculationsPage && this.calculationsPage.totalPages <= 1 && !this.currentFilters.title;
  }

  get shownCalculations(): CustomsCalculation[] {
    const query = this.title.trim().toLowerCase();
    if (!query || !this.searchLocally) {
      return this.calculations;
    }
    return this.calculations.filter(c =>
      this.displayTitle(c).toLowerCase().includes(query)
      || String(c.id).includes(query)
      || (c.products ?? []).some(p => p.nameOriginal?.toLowerCase().includes(query)));
  }

  /** Своё название расчёта, иначе первый товар («Ноутбук и ещё 2»), иначе «Расчёт № 218». */
  displayTitle(c: CustomsCalculation): string {
    if (c.title?.trim()) {
      return c.title;
    }
    const products = c.products ?? [];
    const first = products[0]?.nameOriginal?.trim();
    if (!first) {
      return this.t('myCalcs.calcNo', 'Расчёт №') + ' ' + c.id;
    }
    return products.length > 1
      ? first + this.t('myCalcs.andMore', ' и ещё {n}').replace('{n}', '' + (products.length - 1))
      : first;
  }

  /** Сумма платежей в тенге без копеек: «1 264 964 ₸»; пока не посчитано — прочерк. */
  totalLabel(c: CustomsCalculation): string {
    const total = (c.products ?? []).reduce((sum, p) => sum + (p.totalCustomsPayments || 0), 0);
    return total > 0
      ? new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 0 }).format(Math.round(total)) + ' ₸'
      : '—';
  }

  statusLabel(c: CustomsCalculation): string {
    switch (c.status) {
      case 'IN_PROCESSING': return this.t('myCalcs.statusInProcessing', 'На сопровождении');
      case 'PROCESSED': return this.t('myCalcs.statusProcessed', 'Оформлено');
      default: return this.t('myCalcs.statusCreated', 'Расчёт готов');
    }
  }

  loadCalculations(): void {
    this.loadCalculationsPage(this.currentPage, this.currentFilters);
  }

  loadCalculationsPage(page: number, filters: SearchCustomsCalculationsRequest): void {
    this.loading = true;
    this.error = null;
    this.currentPage = page;
    this.currentFilters = filters;

    this.subscriptions.add(
      this.customsCalculationsService.searchCalculations(filters, page, this.pageSize).subscribe({
        next: (pageResponse) => {
          this.calculationsPage = pageResponse;
          this.calculations = pageResponse.content;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading calculations:', err);
          this.error = this.t('myCalcs.loadError', 'Не удалось загрузить расчёты. Обновите страницу.');
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  applyFilters(): void {
    this.loadCalculationsPage(0, {
      title: this.searchLocally ? undefined : this.title.trim() || undefined,
      dateFrom: this.dateFrom || undefined,
      dateTo: this.dateTo || undefined
    });
  }

  clearFilters(): void {
    this.title = '';
    this.dateFrom = null;
    this.dateTo = null;
    this.filtersOpen = false;
    this.applyFilters();
  }

  onPageChange(page: number): void {
    this.loadCalculationsPage(page, this.currentFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onCreateCalculation(): void {
    this.router.navigate(['/calculations/new']);
  }

  deleteQuestion(id: number): string {
    return this.t('myCalcs.deleteConfirm', 'Удалить расчёт № {id}? Восстановить его не получится.').replace('{id}', '' + id);
  }

  askDelete(id: number): void {
    this.confirmDeleteId = id;
    this.actionError = null;
  }

  onDeleteCalculation(id: number): void {
    this.deleting = true;
    this.subscriptions.add(
      this.customsCalculationsService.deleteCalculation(id).subscribe({
        next: () => {
          this.deleting = false;
          this.confirmDeleteId = null;
          this.loadCalculations();
        },
        error: (err) => {
          console.error('Error deleting calculation:', err);
          // Список не прячем — показываем ошибку над ним
          this.deleting = false;
          this.confirmDeleteId = null;
          this.actionError = this.t('common.deleteError', 'Ошибка при удалении расчета');
          this.cdr.markForCheck();
        }
      })
    );
  }
}
